import React from 'react';
import { LeadForm } from '../components/forms/LeadForm';
import { usePageMeta } from '../hooks/usePageMeta';
import { Mail, Phone, MessageCircle, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'hello@digitalstartup.in',
    link: 'mailto:hello@digitalstartup.in',
  },
  {
    icon: Phone,
    label: 'Phone',
    value: '+91 98679 58759',
    link: 'tel:+919867958759',
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: 'Chat with us',
    link: 'https://wa.me/919867958759?text=Hi! I\'m interested in learning more about your digital marketing services.',
  },
  {
    icon: Clock,
    label: 'Business Hours',
    value: 'Mon-Fri: 10AM - 7PM IST',
    link: null,
  },
];

const reasons = [
  'Free consultation with no commitment',
  'Response within 24 hours',
  'Customized growth strategy',
  'Transparent pricing',
];

export const Contact = () => {
  usePageMeta();
  
  return (
    <main className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#FFAA17] mb-3">
              Contact Us
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] mb-4">
              Let's Grow Your Business Together
            </h1>
            <p className="text-base md:text-lg text-[#525252]">
              Have a question or ready to start? Reach out to us and we'll get back 
              to you within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 md:py-16 bg-[#F7F7F7]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6">Send us a message</h2>
              <LeadForm source="contact_form" />
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6">Get in touch</h2>
              
              <div className="space-y-6 mb-8">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-[#FFAA17]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#525252]">{item.label}</p>
                        {item.link ? (
                          <a
                            href={item.link}
                            target={item.link.startsWith('http') ? '_blank' : undefined}
                            rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="font-semibold text-[#1A1A1A] hover:text-[#FFAA17] transition-colors"
                            data-testid={`contact-${item.label.toLowerCase()}`}
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="font-semibold text-[#1A1A1A]">{item.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-[#25D366]/10 rounded-2xl p-6 mb-8">
                <h3 className="font-semibold text-[#1A1A1A] mb-2">Prefer WhatsApp?</h3>
                <p className="text-[#525252] mb-4 text-sm">
                  Get instant responses on WhatsApp. We're available during business hours.
                </p>
                <Button
                  asChild
                  className="bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full"
                  data-testid="whatsapp-cta"
                >
                  <a
                    href="https://wa.me/919867958759?text=Hi! I'm interested in learning more about your digital marketing services."
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat on WhatsApp
                  </a>
                </Button>
              </div>

              {/* Why Choose Us */}
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-semibold text-[#1A1A1A] mb-4">Why reach out to us?</h3>
                <ul className="space-y-3">
                  {reasons.map((reason, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#FFAA17]" />
                      <span className="text-[#525252]">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
            <div className="bg-gray-100 h-[300px] md:h-[400px] flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-[#FFAA17] mx-auto mb-4" />
                <p className="text-[#525252]">Mumbai, India</p>
                <p className="text-sm text-[#525252] mt-2">We serve clients globally</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
